import { type BridgeCard, highCardPoints } from './cards.js';
import type { BridgeTrump } from './contract.js';
import {
  type BridgeGame,
  type BridgePhase,
  type BridgeCompletedTrick,
  type BridgeTrickPlay,
  actingSeat,
  legalPlays,
} from './play.js';
import { BRIDGE_SEATS, type BridgeControllers, type BridgeSeat, type BridgeSide } from './seats.js';

/** What a seat may know about the contract. The combined points behind it are not included. */
export interface BridgeViewContract {
  readonly declaringSide: BridgeSide;
  readonly declarer: BridgeSeat;
  readonly dummy: BridgeSeat;
  readonly tricksNeeded: number;
  /** Null until the declarer has chosen. */
  readonly trump: BridgeTrump | null;
  /** The engine's suggestion. Only the declarer is told it. */
  readonly suggestedTrump: BridgeTrump | null;
}

/**
 * Everything one seat is allowed to see, and nothing else. A client renders from this and a robot
 * decides from this; the server sends each player only their own view. It holds the viewer's own
 * hand, dummy's hand only once the viewer may see it, and public facts (hand sizes, the contract,
 * the tricks played). It never holds a hidden hand, and tests check that over many deals.
 */
export interface BridgeView {
  readonly viewer: BridgeSeat;
  readonly phase: BridgePhase;
  readonly dealer: BridgeSeat;
  readonly controllers: BridgeControllers;
  /** The viewer's own cards still in hand. */
  readonly hand: readonly BridgeCard[];
  /** Dummy's hand, once the viewer may see it (the declarer from the start; everyone after the opening lead). */
  readonly dummy: { readonly seat: BridgeSeat; readonly hand: readonly BridgeCard[] } | null;
  readonly handSizes: Readonly<Record<BridgeSeat, number>>;
  readonly contract: BridgeViewContract;
  readonly trick: readonly BridgeTrickPlay[];
  readonly tricks: readonly BridgeCompletedTrick[];
  readonly tricksWon: Readonly<Record<BridgeSide, number>>;
  /** The seat whose card is next (the owner of the card; the declarer plays it when that is dummy). */
  readonly turn: BridgeSeat;
  /** The seat that has to act: the declarer chooses trump and plays dummy's cards. Null when done. */
  readonly actor: BridgeSeat | null;
  readonly canAct: boolean;
  /** The cards the viewer may play now. Empty unless it is the viewer's turn to play. */
  readonly legalPlays: readonly BridgeCard[];
  /** High-card points the viewer can count: their own hand, and dummy's once visible. */
  readonly points: { readonly own: number; readonly dummy: number | null };
}

export function viewFor(game: BridgeGame, viewer: BridgeSeat): BridgeView {
  const c = game.contract;
  const sees = (c.dummy !== viewer) && (viewer === c.declarer || game.dummyExposed) && game.phase !== 'done';
  const dummyHand = game.hands[c.dummy];
  const actor = actingSeat(game);
  const canAct = actor === viewer;
  const handSizes = Object.fromEntries(BRIDGE_SEATS.map((s) => [s, game.hands[s].length])) as Record<BridgeSeat, number>;
  return {
    viewer,
    phase: game.phase,
    dealer: game.dealer,
    controllers: game.controllers,
    hand: game.hands[viewer],
    dummy: sees ? { seat: c.dummy, hand: dummyHand } : null,
    handSizes,
    contract: {
      declaringSide: c.declaringSide,
      declarer: c.declarer,
      dummy: c.dummy,
      tricksNeeded: c.tricksNeeded,
      trump: c.trump,
      suggestedTrump: viewer === c.declarer && c.trump === null ? c.suggestedTrump : null,
    },
    trick: game.trick,
    tricks: game.tricks,
    tricksWon: game.tricksWon,
    turn: game.turn,
    actor,
    canAct,
    legalPlays: canAct && game.phase === 'play' ? legalPlays(game) : [],
    points: { own: highCardPoints(game.hands[viewer]), dummy: sees ? highCardPoints(dummyHand) : null },
  };
}
